import axios from 'axios';

const MASTER_API_URL = '/api/safety-master';

// ==========================================
// 1. 마스터 설정 (Config, Categories, Processes)
// ==========================================

// 기준표 가져오기
export const getSafetyConfig = async (token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/config`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 기준표 저장하기
export const saveSafetyConfig = async (data, token, comCode = '') => {
  const res = await axios.post(`${MASTER_API_URL}/config`, data, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 사업장 기본 정보 가져오기
export const getSafetyBasicInfo = async (token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/basic-info`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 사업장 기본 정보 저장하기
export const saveSafetyBasicInfo = async (data, token, comCode = '') => {
  const isFormData = data instanceof FormData;
  const res = await axios.post(`${MASTER_API_URL}/basic-info`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
      'x-com-code': comCode,
      ...(isFormData ? { 'Content-Type': 'multipart/form-data' } : {})
    },
  });
  return res.data;
};

// 대분류 가져오기
export const getSafetyCategories = async (token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/categories`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 대분류 저장하기
export const saveSafetyCategory = async (data, token, comCode = '') => {
  const res = await axios.post(`${MASTER_API_URL}/categories`, data, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 대분류 삭제하기
export const deleteSafetyCategory = async (id, token, comCode = '') => {
  const res = await axios.delete(`${MASTER_API_URL}/categories/${id}`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 소분류 가져오기
export const getRiskCauses = async (categoryId, token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/causes`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
    params: { categoryId }
  });
  return res.data;
};

// 소분류 저장하기
export const saveRiskCause = async (data, token, comCode = '') => {
  const res = await axios.post(`${MASTER_API_URL}/causes`, data, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 소분류 삭제하기
export const deleteRiskCause = async (id, token, comCode = '') => {
  const res = await axios.delete(`${MASTER_API_URL}/causes/${id}`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 공정 마스터 가져오기
export const getSafetyProcessMasters = async (token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/processes`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 공정 마스터 저장하기
export const saveSafetyProcessMaster = async (data, token, comCode = '') => {
  const res = await axios.post(`${MASTER_API_URL}/processes`, data, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 공정 마스터 삭제하기
export const deleteSafetyProcessMaster = async (id, token, comCode = '') => {
  const res = await axios.delete(`${MASTER_API_URL}/processes/${id}`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 마스터 맵핑 가져오기
export const getSafetyMasterMappings = async (token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/hazard-map`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 마스터 맵핑 저장하기
export const saveSafetyMasterMappings = async (data, token, comCode = '') => {
  const res = await axios.post(`${MASTER_API_URL}/hazard-map`, data, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// ==========================================
// 2. 프로젝트 (Assessment)
// ==========================================
const ASSESS_API_URL = '/api/risk-assess';

export const getSafetyProjects = async (token, params = {}) => {
  const res = await axios.get(ASSESS_API_URL, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

// [신규] 초고속 최적화 API 호출 (통계+리스트 한방 처리)
export const getSafetyProjectsOptimized = async (token, params = {}) => {
  const res = await axios.get(`${ASSESS_API_URL}/projects-optimized`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

export const getSafetyProjectYears = async (token) => {
  const res = await axios.get(`${ASSESS_API_URL}/years`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const getSafetySummaryStats = async (token, params = {}) => {
  const res = await axios.get(`${ASSESS_API_URL}/summary-stats`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

export const saveSafetyProject = async (data, token) => {
  const res = await axios.post(ASSESS_API_URL, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

export const deleteSafetyProject = async (id, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// 프로젝트별 공정 목록 가져오기
export const getAssessProcesses = async (assessmentId, token) => {
  const res = await axios.get(`${ASSESS_API_URL}/processes`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { assessmentId }
  });
  return res.data;
};

// 프로젝트별 공정 저장 (추가/수정)
export const saveAssessProcess = async (data, token) => {
  const res = await axios.post(`${ASSESS_API_URL}/processes`, data, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// 프로젝트별 공정 삭제
export const deleteAssessProcess = async (id, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/processes/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
};

// 프로젝트별 매트릭스 체크 데이터 가져오기
export const getAssessMatrixChecks = async (assessmentId, token) => {
  const res = await axios.get(`${ASSESS_API_URL}/matrix-checks`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { assessmentId }
  });
  return res.data;
};

// 프로젝트별 매트릭스 체크 셀 토글 저장
export const toggleAssessMatrixCheck = async (data, token) => {
  const res = await axios.post(`${ASSESS_API_URL}/matrix-checks/toggle`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 개선조치 모아보기 전체 리스트 가져오기 (페이징, 검색, 내담당자 필터 지원)
export const getAssessAllActions = async (token, params = {}) => {
  const res = await axios.get(`${ASSESS_API_URL}/evaluations/actions`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

// [신규] 개선조치 모아보기 초고속 최적화 API 호출
export const getAssessAllActionsOptimized = async (token, params = {}) => {
  const res = await axios.get(`${ASSESS_API_URL}/evaluations/actions-optimized`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

// 프로젝트별 위험성 평가 리스트 가져오기 (페이징, 검색 파라미터 지원)
export const getAssessEvaluations = async (assessmentId, token, extraParams = {}) => {
  const res = await axios.get(`${ASSESS_API_URL}/evaluations`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { assessmentId, ...extraParams }
  });
  return res.data;
};

// 프로젝트별 위험성 평가 정보 저장 (Upsert)
export const saveAssessEvaluation = async (data, token) => {
  const res = await axios.post(`${ASSESS_API_URL}/evaluations`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 프로젝트별 위험성 평가 정보 부분 업데이트 (수정모드)
export const patchAssessEvaluationAction = async (id, data, token) => {
  const res = await axios.patch(`${ASSESS_API_URL}/evaluations/${id}/action`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 프로젝트별 위험성 평가 정보 삭제
export const deleteAssessEvaluation = async (id, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/evaluations/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 조치 사진 업로드
export const uploadAssessPhoto = async (evaluationId, type, file, token) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);

  const res = await axios.post(`${ASSESS_API_URL}/evaluations/${evaluationId}/photos`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
};

// 조치 사진 삭제
export const deleteAssessPhoto = async (photoId, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/evaluations/photos/${photoId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 프로젝트별 결과 총평 회의록 및 교육일지 조회
export const getSafetyResultSummary = async (assessmentId, token) => {
  const res = await axios.get(`${ASSESS_API_URL}/result-summary`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { assessmentId }
  });
  return res.data;
};

// 프로젝트별 결과 총평 회의록 및 교육일지 저장
export const saveSafetyResultSummary = async (data, token) => {
  const res = await axios.post(`${ASSESS_API_URL}/result-summary`, data, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 프로젝트별 결과 총평 삭제 (DELETE)
export const deleteSafetyResultSummary = async (assessmentId, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/result-summary/${assessmentId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 프로젝트 상세 조회 (파일 경로 상태 동기화용)
export const getSafetyProjectDetail = async (id, token) => {
  const res = await axios.get(`${ASSESS_API_URL}/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 프로젝트 증빙 파일 업로드 (교육수료증 전용)
export const uploadSafetyProjectFileEdu = async (id, file, token) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axios.post(`${ASSESS_API_URL}/${id}/upload-file/edu`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
};

// 프로젝트 증빙 파일 삭제 (교육수료증 전용)
export const deleteSafetyProjectFileEdu = async (id, fileUrl, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/${id}/upload-file/edu`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { fileUrl }
  });
  return res.data;
};

// 프로젝트 증빙 파일 업로드 (회의록 서명지 전용)
export const uploadSafetyProjectFilePatrol = async (id, file, token) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await axios.post(`${ASSESS_API_URL}/${id}/upload-file/patrol`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
};

// 프로젝트 증빙 파일 삭제 (회의록 서명지 전용)
export const deleteSafetyProjectFilePatrol = async (id, fileUrl, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/${id}/upload-file/patrol`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { fileUrl }
  });
  return res.data;
};

// 프로젝트 상태 업데이트 API
export const updateSafetyProjectStatus = async (id, status, token) => {
  const res = await axios.put(`${ASSESS_API_URL}/${id}/status`, { status }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 교육일지 목록 조회
export const getSafetyTrainings = async (assessmentId, token, searchCategory, searchQuery) => {
  const params = { assessmentId };
  if (searchQuery) {
    params.searchCategory = searchCategory || 'ALL';
    params.searchQuery = searchQuery;
  }
  const res = await axios.get(`${ASSESS_API_URL}/trainings`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

// 교육일지 저장/수정
export const saveSafetyTraining = async (data, token) => {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (key === 'files' && Array.isArray(data.files)) {
      data.files.forEach(f => {
        if (f.isNew && f.file) {
          formData.append('files', f.file);
        } else {
          formData.append('existingFiles', JSON.stringify(f));
        }
      });
    } else {
      if (data[key] !== null && data[key] !== undefined) {
        if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    }
  });

  const res = await axios.post(`${ASSESS_API_URL}/trainings`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
};

// 교육일지 삭제
export const deleteSafetyTraining = async (id, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/trainings/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 회의록 목록 조회
export const getSafetyMeetings = async (assessmentId, token, searchCategory, searchQuery) => {
  const params = { assessmentId };
  if (searchQuery) {
    params.searchCategory = searchCategory || 'ALL';
    params.searchQuery = searchQuery;
  }
  const res = await axios.get(`${ASSESS_API_URL}/meetings`, {
    headers: { Authorization: `Bearer ${token}` },
    params
  });
  return res.data;
};

// 회의록 저장/수정
export const saveSafetyMeeting = async (data, token) => {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (key === 'files' && Array.isArray(data.files)) {
      data.files.forEach(f => {
        if (f.isNew && f.file) {
          formData.append('files', f.file);
        } else {
          formData.append('existingFiles', JSON.stringify(f));
        }
      });
    } else {
      if (data[key] !== null && data[key] !== undefined) {
        if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    }
  });

  const res = await axios.post(`${ASSESS_API_URL}/meetings`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    }
  });
  return res.data;
};

// 회의록 삭제
export const deleteSafetyMeeting = async (id, token) => {
  const res = await axios.delete(`${ASSESS_API_URL}/meetings/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// 종합보고서 통합 데이터 조회
export const getSafetyFullReport = async (projectId, token) => {
  const res = await axios.get(`${ASSESS_API_URL}/${projectId}/full-report`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// ==========================================
// 사전준비 마스터 (Preparation Master)
// ==========================================

// 사전준비 마스터 조회
export const getSafetyPreparation = async (token, comCode = '') => {
  const res = await axios.get(`${MASTER_API_URL}/preparation`, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};

// 사전준비 마스터 저장
export const saveSafetyPreparation = async (data, token, comCode = '') => {
  const res = await axios.post(`${MASTER_API_URL}/preparation`, data, {
    headers: { Authorization: `Bearer ${token}`, 'x-com-code': comCode },
  });
  return res.data;
};
